import React from 'react';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

type Order = Database['public']['Tables']['orders']['Row'] & {
  display_id?: number;
  delivery_date?: string;
  order_items: Array<{
    quantity: number;
    unit_price: number;
    price_at_time?: number;
    products: {
      name: string;
    };
  }>;
};

type OrderedProduct = {
  name: string;
  quantity: number;
  sum: number;
};

type OrderedProductsByDate = {
  [date: string]: {
    products: OrderedProduct[];
    totalSum: number;
  };
};

export function OrderHistory() {
  const { user } = useAuth();
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [orderedProductsByDate, setOrderedProductsByDate] = React.useState<OrderedProductsByDate>({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // If no user is logged in, exit early
    if (!user) {
      setLoading(false);
      return;
    }

    const userId = user.id;
    if (!userId) {
      setLoading(false);
      return;
    }

    async function fetchOrders() {
      try {
        // First get all producer's products
        const { data: products, error: productsError } = await supabase
          .from('products')
          .select('id')
          .eq('producer_id', userId);

        if (productsError) throw productsError;
        if (!products?.length) {
          setOrders([]);
          setLoading(false);
          return;
        }

        // Then get order items for these products
        const { data: orderItems, error: orderItemsError } = await supabase
          .from('order_items')
          .select(`
            order_id,
            quantity,
            unit_price,
            price_at_time,
            products (
              name
            )
          `)
          .in('product_id', products.map(p => p.id));

        if (orderItemsError) throw orderItemsError;
        if (!orderItems?.length) {
          setOrders([]);
          setLoading(false);
          return;
        }

        // Get historical orders (delivery date is in the past)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
        const todayISOString = today.toISOString();

        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('id, display_id, created_at, delivery_date, status, total_amount')
          .in('id', orderItems.map(item => item.order_id))
          .lt('delivery_date', todayISOString) // Only get past orders
          .order('delivery_date', { ascending: false }); // Most recent first

        if (ordersError) throw ordersError;

        // Combine the data
        const ordersWithItems = orders.map(order => ({
          ...order,
          order_items: orderItems.filter(item => item.order_id === order.id)
        }));

        setOrders(ordersWithItems);

        // Process ordered products by date
        const productsByDate: OrderedProductsByDate = {};
        
        ordersWithItems.forEach(order => {
          // Use delivery_date if available, otherwise fallback to created_at
          const dateToUse = order.delivery_date || order.created_at;
          // Format the date using Swedish locale
          const date = new Date(dateToUse).toLocaleDateString('sv-SE');
          
          if (!productsByDate[date]) {
            productsByDate[date] = {
              products: [],
              totalSum: 0
            };
          }
          
          order.order_items.forEach(item => {
            const existingProduct = productsByDate[date].products.find(
              p => p.name === item.products.name
            );
            
            // Use price_at_time if available (for weight-based products) or fall back to unit_price
            const priceToUse = item.price_at_time || item.unit_price;
            const itemSum = item.quantity * priceToUse;
            
            if (existingProduct) {
              existingProduct.quantity += item.quantity;
              existingProduct.sum += itemSum;
            } else {
              productsByDate[date].products.push({
                name: item.products.name,
                quantity: item.quantity,
                sum: itemSum
              });
            }
            
            productsByDate[date].totalSum += itemSum;
          });
        });
        
        setOrderedProductsByDate(productsByDate);
      } catch (err) {
        logger.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
  }, [user]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <Link to="/producer/orders" className="flex items-center text-green-600 hover:text-green-800">
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span>Tillbaka till kommande leveranser</span>
        </Link>
      </div>

      <h1 className="text-2xl font-semibold text-gray-900">Orderhistorik</h1>
      
      {/* Ordered Products by Date - Historical */}
      {Object.keys(orderedProductsByDate).length > 0 ? (
        <div className="mt-8">
          {Object.entries(orderedProductsByDate)
            .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
            .map(([date, data]) => (
              <div key={date} className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h2 className="text-lg font-medium text-gray-900">Leveransdatum: {date}</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Totalt ordervärde: {(data.totalSum / 1.12).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr
                  </p>
                </div>
                <div className="flow-root">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                              Produkt
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Antal
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Summa (ex moms)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.products
                            .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
                            .map((product, index) => (
                            <tr key={index}>
                              <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {product.name}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {product.quantity}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                {(product.sum / 1.12).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white shadow rounded-lg mt-6">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen orderhistorik</h3>
          <p className="mt-1 text-sm text-gray-500">
            Tidigare leveranser och ordrar visas här när de finns tillgängliga.
          </p>
        </div>
      )}
    </div>
  );
} 